/**
 * RMT — marketplace de alz.
 * Endpoints de admin (/admin/rmt/*) e do vendedor logado (/rmt/*).
 */
import { apiGet, apiPost, apiPut, apiPatch, apiDelete } from './api';
import { API_URL } from './authServices';

/* ───────────────────────────── Admin ───────────────────────────── */

export interface RmtSellLimits {
  limitAlzDay: number;
  limitAlzWeek: number;
  limitAlzMonth: number;
  limitSalesDay: number;
  limitSalesWeek: number;
  limitSalesMonth: number;
}

export interface RmtItemConfig {
  itemsEnabled: boolean;
  alzPriceMinBrl: number;
  alzPriceMaxBrl: number;
  itemPriceMinBrl: number;
  itemPriceMaxBrl: number;
}

export interface RmtConfig extends RmtSellLimits, RmtItemConfig {
  cofreCharIdx: number | null;
  cofreCharName: string | null;
  depositEnabled: boolean;
  minDepositAlz: number;
}

export interface RmtConfigUpdate extends RmtSellLimits, RmtItemConfig {
  depositEnabled: boolean;
  minDepositAlz: number;
}

export interface RmtBlacklistItem {
  blacklistId: number;
  itemId: number;
  itemOption: number | null;
  reason: string | null;
  createdAt: string | null;
}

export interface RmtPriceLimit {
  itemId: number;
  minPriceBrl: number;
  maxPriceBrl: number;
  note: string | null;
}

export interface RmtSeller {
  userNum: number;
  accountId: string | null;
  sellerName: string | null;
  pixKey: string | null;
  pixKeyType: string | null;
  docStatus: string;   // pending | approved | rejected
  status: string;      // active | suspended
  balanceAlz: number;
  grantedBy: number | null;
  grantedById: string | null;
  grantedAt: string | null;
  notes: string | null;
}

export interface RmtAccountSearch {
  userNum: number;
  accountId: string;
  email: string | null;
  isSeller: boolean;
}

export interface RmtCharSearch {
  characterIdx: number;
  name: string;
  userNum: number;
  online: boolean;
}

export interface RmtScanResult {
  enabled: boolean;
  processed: number;
  totalAlz: number;
}

export interface RmtSellerUpdate {
  status?: string;
  docStatus?: string;
  notes?: string;
}

export const getRmtConfig = () => apiGet<RmtConfig>('/admin/rmt/config');

export const updateRmtConfig = (input: RmtConfigUpdate) =>
  apiPut<RmtConfig>('/admin/rmt/config', input);

export const setRmtCofre = (characterIdx: number) =>
  apiPost<RmtConfig>('/admin/rmt/config/cofre', { characterIdx });

export const searchRmtChar = (q: string) =>
  apiGet<RmtCharSearch[]>('/admin/rmt/char-search', { q });

export const runRmtScan = () => apiPost<RmtScanResult>('/admin/rmt/scan', {});

export const listRmtSellers = () => apiGet<RmtSeller[]>('/admin/rmt/sellers');

export const searchRmtAccount = (q: string) =>
  apiGet<RmtAccountSearch[]>('/admin/rmt/sellers/search', { q });

export const grantRmtSeller = (userNum: number, notes?: string) =>
  apiPost<RmtSeller>(`/admin/rmt/sellers/${userNum}`, { notes });

export const updateRmtSeller = (userNum: number, input: RmtSellerUpdate) =>
  apiPatch<RmtSeller>(`/admin/rmt/sellers/${userNum}`, input);

export const revokeRmtSeller = (userNum: number) =>
  apiDelete<{ status: string; message: string }>(`/admin/rmt/sellers/${userNum}`);

export const getRmtBlacklist = () => apiGet<RmtBlacklistItem[]>('/admin/rmt/blacklist');

export const addRmtBlacklist = (itemId: number, itemOption: number | null, reason: string) =>
  apiPost<RmtBlacklistItem>('/admin/rmt/blacklist', { itemId, itemOption, reason: reason || undefined });

export const removeRmtBlacklist = (blacklistId: number) =>
  apiDelete<{ status: string }>(`/admin/rmt/blacklist/${blacklistId}`);

export const getRmtPriceLimits = () => apiGet<RmtPriceLimit[]>('/admin/rmt/price-limits');

export const upsertRmtPriceLimit = (
  itemId: number,
  input: { minPriceBrl: number; maxPriceBrl: number; note?: string },
) => apiPut<RmtPriceLimit>(`/admin/rmt/price-limits/${itemId}`, input);

export const removeRmtPriceLimit = (itemId: number) =>
  apiDelete<{ status: string }>(`/admin/rmt/price-limits/${itemId}`);

/* ─────────────────────────── Vendedor ──────────────────────────── */

export interface RmtMe {
  isSeller: boolean;
  status: string | null;
  balanceAlz: number;
  availableAlz: number;
  alzPriceBrl: number;
  sellerName: string | null;
  pixKey: string | null;
  pixKeyType: string | null;
  docStatus: string | null;
  cofreCharName: string | null;
  minDepositAlz: number;
  throttled: boolean;
  throttleReason: string | null;
  reservedAlz: number;
  soldAlz30d: number;
  earnedBrl30d: number;
  totalSales: number;
  totalSoldAlz: number;
  totalEarnedBrl: number;
  pendingPayoutBrl: number;
  bestCompetitorPrice: number | null;
  commissionPct: number;
  sandbox: boolean;
  unitAlz?: number;              // lote de venda (config 'unit_alz'); a oferta tem que ser múltiplo
}

export interface RmtDeposit {
  depositId: number;
  mailId: number;
  senderCharIdx: number;
  senderName: string | null;
  alzAmount: number;
  mailDate: string | null;
  detectedAt: string;
}

export interface RmtLedgerEntry {
  ledgerId: number;
  entryType: string;   // deposit | sale | withdrawal | adjustment
  alzDelta: number;
  balanceAfter: number;
  refType: string | null;
  refId: number | null;
  createdAt: string;
  notes: string | null;
}

export interface RmtSellerConfigUpdate {
  sellerName?: string;
  pixKey?: string;
  pixKeyType?: string;
}

export const getRmtMe = () => apiGet<RmtMe>('/rmt/me');

/** Dispara o scan de deposito on-demand e devolve o status atualizado. */
export const refreshRmt = () => apiPost<RmtMe>('/rmt/refresh', {});

export const getRmtDeposits = (limit = 100) =>
  apiGet<RmtDeposit[]>('/rmt/deposits', { limit });

export const getRmtLedger = (limit = 100) =>
  apiGet<RmtLedgerEntry[]>('/rmt/ledger', { limit });

export const updateRmtSellerConfig = (input: RmtSellerConfigUpdate) =>
  apiPut<RmtMe>('/rmt/config', input);

/* ─────────────────────── Marketplace (compra) ──────────────────── */

export type RmtPeriod = '7d' | '30d' | '1y';

export interface RmtStatPoint {
  label: string;
  price: number;
  volumeAlz: number;
  trades: number;
}

export interface RmtTrade {
  at: string;
  alzAmount: number;
  unitPrice: number;
  totalBrl: number;
}

export interface RmtMarketStats {
  period: string;
  points: RmtStatPoint[];
  alzInOffer: number;
  minPrice: number;
  maxPrice: number;
  oscillationPct: number;
  unitAlz: number;
  unitPriceBrl: number;
  bestPrice: number;
  lastPrice: number;
  sellersActive: number;
  volumeAlz24h: number;
  volumeAlz7d: number;
  volumeAlz30d: number;
  trades24h: number;
  trades7d: number;
  trades30d: number;
  totalAlzTraded: number;
  totalTrades: number;
  recentTrades: RmtTrade[];
  sandbox: boolean;
  commissionPct: number;
}

export interface RmtBuyResult {
  orderId: number;
  units: number;
  alzAmount: number;
  charName: string;
  vendorName: string;
  priceBrl: number;
  message: string;
  status: 'completed' | 'awaiting_payment';
  txid: string | null;
  qrCodePayload: string | null;
  qrCodeImage: string | null;
  expiresIn: number | null;
}

export interface RmtVendor {
  userNum: number;
  sellerName: string;
  availableAlz: number;
  alzPriceBrl: number;
}

export interface RmtItemMarket {
  listingId: number;
  baseItemId: number | null;
  itemName: string | null;
  upgrade: number;
  extreme: number;
  divine: number;
  slotCount: number;
  bound: boolean;
  sealed: boolean;
  priceBrl: number;
  sellerUserNum: number;
  sellerName: string;
  depositedAt: string;
}

export interface RmtBuyItemResult {
  listingId: number;
  itemName: string;
  charName: string;
  vendorName: string;
  priceBrl: number;
  message: string;
}

export interface RmtOrder {
  orderId: number;
  units: number;
  alzAmount: number;
  priceBrl: number;
  charName: string | null;
  status: string;
  isSandbox: boolean;
  createdAt: string;
  deliveredAt: string | null;
  vendorName: string | null;
  expiresAt: string | null;
  pixCopiaCola: string | null;
}

export interface RmtPayment {
  orderId: number;
  status: string;   // awaiting_payment | completed | expired | cancelled | paid_no_stock
  priceBrl: number;
  alzAmount: number;
  charName: string | null;
  qrCodePayload: string | null;
  qrCodeImage: string | null;
  expiresIn: number | null;
}

export const getMarketStats = (period: RmtPeriod) =>
  apiGet<RmtMarketStats>('/rmt/stats', { period });

export const getVendors = () => apiGet<RmtVendor[]>('/rmt/vendors');

export const buyAlz = (units: number, charIdx: number, vendorUserNum: number, expectedPrice?: number) =>
  apiPost<RmtBuyResult>('/rmt/buy', { units, charIdx, vendorUserNum, expectedPrice });

/** Polling do Pix de um pedido (qr=true devolve a imagem do QR de novo). */
export const getOrderPayment = (orderId: number, qr = false) =>
  apiGet<RmtPayment>(`/rmt/order/${orderId}/payment`, qr ? { qr: 1 } : undefined);

export const cancelOrder = (orderId: number) =>
  apiPost<RmtPayment>(`/rmt/order/${orderId}/cancel`, {});

export const getMyOrders = (limit = 50) =>
  apiGet<RmtOrder[]>('/rmt/orders', { limit });

/** Itens à venda no marketplace. */
export const getItems = () => apiGet<RmtItemMarket[]>('/rmt/items');

/** Compra um item, entregue no personagem escolhido. */
export const buyItem = (listingId: number, charIdx: number) =>
  apiPost<RmtBuyItemResult>('/rmt/buy-item', { listingId, charIdx });

/* ──────────────────── Vendedor — itens e alz à venda ──────────────────── */

export interface RmtItemListing {
  listingId: number;
  baseItemId: number | null;
  itemName: string | null;
  itemKindIdx: number;
  itemOption: number;
  itemDurationIdx: number;
  upgrade: number;
  extreme: number;
  divine: number;
  slotCount: number;
  bound: boolean;
  sealed: boolean;
  priceBrl: number;
  enabled: boolean;
  status: string;
  blacklisted: boolean;
  depositedAt: string;
}

/** Vendedor define quanto alz fica à venda e o preço por 100kk. */
export const setSellerAlz = (availableAlz: number, alzPriceBrl: number) =>
  apiPut<RmtMe>('/rmt/seller/alz', { availableAlz, alzPriceBrl });

/** Itens depositados pelo vendedor (pra precificar/habilitar). */
export const getSellerItems = () => apiGet<RmtItemListing[]>('/rmt/seller/items');

/** Define preço (BRL) e habilita/desabilita um item. */
export const updateItemListing = (listingId: number, priceBrl: number, enabled: boolean) =>
  apiPut<RmtItemListing>(`/rmt/seller/items/${listingId}`, { priceBrl, enabled });


/* ──────────────────── Visibilidade (Modo Teste) ──────────────────── */

export interface RmtVisibility {
  enabled: boolean;    // mostra o menu RMT pra esta conta
  testMode: boolean;
  sandbox: boolean;
  isGm: boolean;
}

export const getRmtVisibility = () => apiGet<RmtVisibility>('/rmt/visibility');

/* ─────────────── Solicitação pra virar vendedor ─────────────── */

export interface RmtApplyInfo {
  isSeller: boolean;
  requireDocs: boolean;
  requireCellphone: boolean;
  hasCellphone: boolean;
  pixTypes: string[];
  maxFiles: number;
  applicationStatus: 'pending' | 'approved' | 'rejected' | null;
  rejectReason: string | null;
  appliedAt: string | null;
  sellerName: string | null;
}

export const getApplyInfo = () => apiGet<RmtApplyInfo>('/rmt/apply');

/** multipart: sellerName, pixKey, pixKeyType + files[] */
export async function applySeller(input: {
  sellerName: string; pixKey: string; pixKeyType: string; files: File[];
}): Promise<RmtApplyInfo> {
  const fd = new FormData();
  fd.append('sellerName', input.sellerName);
  fd.append('pixKey', input.pixKey);
  fd.append('pixKeyType', input.pixKeyType);
  input.files.forEach(f => fd.append('files', f));
  let auth: Record<string, string> = {};
  try {
    const raw = localStorage.getItem('liberty_auth');
    const tok = raw ? JSON.parse(raw).token : null;
    if (tok) auth = { Authorization: `Bearer ${tok}` };
  } catch { /* ignore */ }
  const res = await fetch(`${API_URL}/rmt/apply`, { method: 'POST', headers: auth, body: fd });
  if (!res.ok) throw await res.json().catch(() => ({ message: 'Erro de conexao' }));
  return res.json();
}

/* ──────────────────── Vendedor — saque / vendas ──────────────────── */

export interface RmtSale {
  orderId: number;
  alzAmount: number;
  priceBrl: number;
  payoutBrl: number;
  status: string;
  payoutStatus: string;   // none | pending | sending | sent | done | failed | manual
  isSandbox: boolean;
  createdAt: string;
}

export const withdrawAlz = (alz: number, charIdx: number) =>
  apiPost<RmtMe>('/rmt/seller/withdraw', { alz, charIdx });

export const getSellerSales = (limit = 50) => apiGet<RmtSale[]>('/rmt/seller/sales', { limit });

/* ──────────────────── Admin — Mercado de ALZ ──────────────────── */

export interface RmtApplication {
  applicationId: number;
  userNum: number;
  accountId: string | null;
  sellerName: string;
  pixKey: string;
  pixKeyType: string;
  docFiles: string[];
  status: 'pending' | 'approved' | 'rejected';
  rejectReason: string | null;
  createdAt: string;
  reviewedById: string | null;
  reviewedAt: string | null;
  hasCellphone: boolean;
  accountCreated: string | null;
  charCount: number;
  maxLevel: number;
}

export interface RmtDayPoint { day: string; alz: number; brl: number; commission: number; trades: number }
export interface RmtRank { userNum: number; name: string | null; alz: number; brl: number; trades: number }

export interface RmtMetrics {
  totalTrades: number; totalAlz: number; totalBrl: number; totalCommission: number;
  realTrades: number; realBrl: number; realCommission: number; sandboxTrades: number;
  trades30d: number; alz30d: number; brl30d: number; commission30d: number;
  avgPrice: number | null;
  escrowAlz: number; listedAlz: number; reservedAlz: number;
  sellersActive: number; sellersListing: number; bestPrice: number | null;
  pendingApplications: number; awaitingOrders: number;
  payoutPendingBrl: number; payoutFailed: number; paidNoStock: number; deposits30dAlz: number;
  series: RmtDayPoint[]; topSellers: RmtRank[]; topBuyers: RmtRank[];
}

export interface RmtAdminOrder {
  orderId: number; createdAt: string;
  buyerUserNum: number; buyerAccount: string | null; buyerChar: string | null;
  sellerUserNum: number | null; sellerName: string | null;
  alzAmount: number; priceBrl: number; commissionBrl: number; payoutBrl: number;
  status: string; isSandbox: boolean; paidAt: string | null;
  payoutStatus: string; payoutKey: string | null; payoutTries: number;
  payoutError: string | null; payoutAt: string | null; txid: string | null;
}

export interface RmtListing {
  userNum: number; accountId: string | null; sellerName: string | null; status: string;
  balanceAlz: number; availableAlz: number; reservedAlz: number; alzPriceBrl: number;
  pixKey: string | null; pixKeyType: string | null;
  soldAlz: number; soldBrl: number; trades: number; lastDepositAt: string | null;
}

export interface RmtMarketConfig {
  testMode: boolean;
  sandboxMode: boolean;
  payoutAuto: boolean;
  commissionPct: number;
  unitAlz: number;
  unitPriceBrl: number;
  pixExpireSec: number;
  sellerRequireDocs: boolean;
  sellerRequireCellphone: boolean;
  sellerPixTypes: 'cpf' | 'all';
  efiReady?: boolean;
}

export const getRmtApplications = (status?: string) =>
  apiGet<RmtApplication[]>('/admin/rmt/applications', status ? { status } : undefined);
export const approveRmtApplication = (id: number) =>
  apiPost<RmtApplication>(`/admin/rmt/applications/${id}/approve`, {});
export const rejectRmtApplication = (id: number, reason: string) =>
  apiPost<RmtApplication>(`/admin/rmt/applications/${id}/reject`, { reason });

/** Baixa um documento (rota autenticada) e devolve um blob URL pra abrir. */
export async function fetchRmtDoc(id: number, name: string): Promise<string> {
  let auth: Record<string, string> = {};
  try {
    const raw = localStorage.getItem('liberty_auth');
    const tok = raw ? JSON.parse(raw).token : null;
    if (tok) auth = { Authorization: `Bearer ${tok}` };
  } catch { /* ignore */ }
  const res = await fetch(`${API_URL}/admin/rmt/applications/${id}/doc/${encodeURIComponent(name)}`, { headers: auth });
  if (!res.ok) throw new Error('Arquivo indisponível');
  return URL.createObjectURL(await res.blob());
}

export const getRmtMetrics = () => apiGet<RmtMetrics>('/admin/rmt/metrics');
export const getRmtAdminOrders = (params?: { status?: string; payout?: string }) =>
  apiGet<RmtAdminOrder[]>('/admin/rmt/orders', params as Record<string, string> | undefined);
export const rmtOrderPayout = (id: number) => apiPost<RmtAdminOrder>(`/admin/rmt/orders/${id}/payout`, {});
export const rmtOrderPayoutManual = (id: number) =>
  apiPost<RmtAdminOrder>(`/admin/rmt/orders/${id}/payout-manual`, {});
export const rmtOrderCancel = (id: number) => apiPost<RmtAdminOrder>(`/admin/rmt/orders/${id}/cancel`, {});
export const getRmtListings = () => apiGet<RmtListing[]>('/admin/rmt/listings');
export const rmtDelist = (userNum: number) => apiPost<RmtListing>(`/admin/rmt/sellers/${userNum}/delist`, {});
export const rmtReturnEscrow = (userNum: number, reason?: string, charIdx?: number) =>
  apiPost<RmtListing>(`/admin/rmt/sellers/${userNum}/return`, { reason, charIdx });
export const rmtSetPix = (userNum: number, pixKey: string, pixKeyType: string) =>
  apiPut<RmtListing>(`/admin/rmt/sellers/${userNum}/pix`, { pixKey, pixKeyType });
export const getRmtMarketConfig = () => apiGet<RmtMarketConfig>('/admin/rmt/market-config');
export const putRmtMarketConfig = (cfg: RmtMarketConfig) =>
  apiPut<RmtMarketConfig>('/admin/rmt/market-config', cfg);
